import { getAuth } from 'firebase/auth';
import { CLOUD_RUN_CONFIG } from '../config/cloudRun';
import { logger } from './logger';

interface VoiceoverClipInput {
  id: string;
  videoPath?: string;
  videoDuration?: number;
  isCameraSource?: boolean;
  label?: string;
}

interface ProcessVideoRequest {
  voiceoverNotableSegmentClips: VoiceoverClipInput[];
  lessonId?: string;
  // When provided, Cloud Run handles the full delivery pipeline server-side
  originalVideoId?: string;
  draftId?: string;
  coachNotes?: string;
  attachedDrills?: Record<string, unknown>[];
}

interface ProcessVideoResponse {
  success: boolean;
  videoUrl: string;
  storagePath: string;
}

interface TranscodeClipResponse {
  outputPath: string;
}

/**
 * Transcode a Storage clip to H.264 MP4 via Cloud Run.
 * Returns the output storage path (MP4). If the input is already .mp4, returns it unchanged.
 */
export async function transcodeClip(inputPath: string): Promise<string> {
  if (inputPath.endsWith('.mp4')) return inputPath;

  const auth = getAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('User not authenticated');

  const idToken = await currentUser.getIdToken();
  const response = await fetch(`${CLOUD_RUN_CONFIG.VIDEO_PROCESSOR_URL}/transcode-clip`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({ inputPath }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `Transcode request failed: ${response.status}`);
  }

  const data: TranscodeClipResponse = await response.json();
  return data.outputPath;
}

/**
 * Request server-side moov atom relocation (faststart) for a my-cloud MP4 recording.
 * Stream-copy only — no re-encode. Overwrites the file in place.
 * Returns true if the file was rewritten, false if it was already web-optimized.
 */
export async function optimizeCloudRecording(storagePath: string): Promise<boolean> {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('User not authenticated');

  const idToken = await currentUser.getIdToken();
  const response = await fetch(`${CLOUD_RUN_CONFIG.VIDEO_PROCESSOR_URL}/optimize-cloud-recording`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({ storagePath }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `Optimization request failed: ${response.status}`);
  }

  const data = await response.json() as { optimized: boolean };
  return data.optimized;
}

/**
 * Process video using either Cloud Function (emulator/local dev) or Cloud Run (production)
 * Automatically detects which environment to use
 */
export async function processVideo(
  request: ProcessVideoRequest,
): Promise<ProcessVideoResponse> {
  // const useCloudRun = shouldUseCloudRun();

  // if (useCloudRun) {
    logger.info('Using Cloud Run for video processing');
    return processVideoCloudRun(request);
  // } else {
  //   logger.info('Using Cloud Function for video processing');
  //   return processVideoCloudFunction(request);
  // }
}

/**
 * Process video using Cloud Function (for emulator and local development)
 */
// async function processVideoCloudFunction(
//   request: ProcessVideoRequest
// ): Promise<ProcessVideoResponse> {
//   // 60 minute timeout for video processing
//   const processVideoFn = httpsCallable(functions, 'processVideo', {
//     timeout: 60 * 60 * 1000, // 60 minutes in milliseconds
//   });
//   const result = await processVideoFn(request);
//   return result.data as ProcessVideoResponse;
// }

/**
 * Process video using Cloud Run service (for production)
 */
async function processVideoCloudRun(
  request: ProcessVideoRequest
): Promise<ProcessVideoResponse> {
  const auth = getAuth();
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error('User not authenticated');
  }

  // Get Firebase ID token for authentication
  const idToken = await currentUser.getIdToken();

  // Call Cloud Run endpoint
  const response = await fetch(`${CLOUD_RUN_CONFIG.VIDEO_PROCESSOR_URL}/process-video-v2`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `Cloud Run request failed: ${response.status}`);
  }

  return await response.json();
}
